import { useState, useRef, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { useImageEditor } from "./image-editor/ImageEditorProvider";
import { MessageCircle, X, Send, Sparkles, CalendarCheck, Bot, ArrowLeft, Loader2, Paperclip } from "lucide-react";

const SupportChat = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // null=choose, "ai", "appointment", "support"
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "", type: "support" });
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachFileInputRef = useRef(null);

  // AI Chat state
  const [aiMessages, setAiMessages] = useState([
    { role: "assistant", text: "Hi! I'm the StardomFans.com assistant. How can I help you today?" }
  ]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}`);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiMessages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/messages`, { ...form, type: mode || "support", attachment_url: attachmentUrl });
      setSent(true);
      setAttachmentUrl(null);
      toast.success("Message sent! We'll get back to you soon.");
    } catch {
      toast.error("Failed to send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const editImage = useImageEditor();
  const handleAttachFile = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only images supported"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    file = await editImage(file, { maxBytes: 5 * 1024 * 1024 });
    if (!file) { e.target.value = ""; return; }
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Public attachment endpoint (no auth required)
      const res = await axios.post(`${API}/support/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setAttachmentUrl(res.data.url || res.data.media_url);
      toast.success("Image attached");
    } catch {
      toast.error("Upload failed. Try again or send without attachment.");
    } finally {
      setUploadingAttachment(false);
      if (attachFileInputRef.current) attachFileInputRef.current.value = "";
    }
  };

  const sendAiMessage = async () => {
    if (!aiInput.trim() || aiLoading) return;
    const userMsg = aiInput.trim();
    setAiInput("");
    setAiMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/support/ai-chat`, { message: userMsg, session_id: sessionId });
      setAiMessages(prev => [...prev, { role: "assistant", text: res.data.response }]);
    } catch {
      setAiMessages(prev => [...prev, { role: "assistant", text: "Sorry, I'm having trouble connecting. Please try the support form." }]);
    } finally {
      setAiLoading(false);
    }
  };

  const reset = () => {
    setSent(false);
    setMode(null);
    setForm({ name: "", email: "", message: "", type: "support" });
  };

  const startMode = (m) => {
    setMode(m);
    if (m === "appointment") {
      setForm({ ...form, type: "appointment", message: "I'd like to book an appointment to discuss a custom-built account." });
    }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(!open); if (!open) reset(); }}
        data-testid="support-chat-toggle"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
          open ? "bg-[#27272A] rotate-90 scale-90" : "bg-[#EC008C] hover:scale-110"
        }`}
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-[#000000]" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] rounded-2xl overflow-hidden shadow-2xl border border-[#27272A] animate-in slide-in-from-bottom-4 fade-in duration-200"
          data-testid="support-chat-panel"
        >
          {/* Header */}
          <div className="bg-[#EC008C] px-5 py-3.5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#000000] flex items-center justify-center">
                {mode === "ai" ? <Bot className="w-5 h-5 text-[#EC008C]" /> : <Sparkles className="w-5 h-5 text-[#EC008C]" />}
              </div>
              <div>
                <p className="font-['Unbounded'] font-bold text-[#000000] text-sm">StardomFans.com</p>
                <p className="text-[#000000]/60 text-xs">{mode === "ai" ? "AI Assistant" : "Support"}</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="bg-[#000000]">
            {sent ? (
              <div className="text-center py-8 px-5">
                <div className="w-14 h-14 rounded-full bg-[#EC008C]/10 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-7 h-7 text-[#EC008C]" />
                </div>
                <h3 className="font-['Unbounded'] text-lg font-bold text-white mb-2">
                  {mode === "appointment" ? "Appointment Requested!" : "Message Sent!"}
                </h3>
                <p className="text-[#A1A1AA] text-sm mb-4">We'll get back to you at {form.email}</p>
                <button onClick={reset} className="text-[#EC008C] text-sm font-medium hover:underline" data-testid="send-another-btn">
                  Send another message
                </button>
              </div>
            ) : mode === "ai" ? (
              /* AI Chat Mode */
              <div className="flex flex-col h-[380px]">
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {aiMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-[#EC008C] text-[#000000] rounded-br-sm"
                          : "bg-[#1A1A1F] text-[#E4E4E7] border border-[#27272A] rounded-bl-sm"
                      }`}>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {aiLoading && (
                    <div className="flex justify-start">
                      <div className="bg-[#1A1A1F] border border-[#27272A] px-4 py-3 rounded-2xl rounded-bl-sm">
                        <Loader2 className="w-4 h-4 text-[#EC008C] animate-spin" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div className="border-t border-[#27272A] p-3 flex gap-2">
                  <button onClick={() => setMode(null)} className="px-2 text-[#A1A1AA] hover:text-white" data-testid="ai-back-btn">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <input
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendAiMessage()}
                    placeholder="Ask me anything..."
                    className="flex-1 bg-[#121214] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50"
                    data-testid="ai-chat-input"
                  />
                  <button
                    onClick={sendAiMessage}
                    disabled={aiLoading || !aiInput.trim()}
                    className="bg-[#EC008C] text-[#000000] rounded-lg px-3 py-2 hover:bg-[#FF3399] disabled:opacity-50"
                    data-testid="ai-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : !mode ? (
              /* Mode Selection */
              <div className="p-5 space-y-3">
                <p className="text-white text-sm font-medium mb-4">How can we help you today?</p>
                <button onClick={() => startMode("ai")} data-testid="ai-chat-mode-btn"
                  className="w-full flex items-center gap-3 p-4 rounded-xl bg-[#121214] border border-[#27272A] hover:border-[#EC008C]/40 transition-all text-left group"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#EC008C]/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-[#EC008C]" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm group-hover:text-[#EC008C] transition-colors">Chat with AI</p>
                    <p className="text-[#71717A] text-xs">Get instant answers to your questions</p>
                  </div>
                </button>
                <button onClick={() => startMode("appointment")} data-testid="appointment-mode-btn"
                  className="w-full flex items-center gap-3 p-4 rounded-xl bg-[#121214] border border-[#27272A] hover:border-[#D14A85]/40 transition-all text-left group"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#D14A85]/10 flex items-center justify-center flex-shrink-0">
                    <CalendarCheck className="w-5 h-5 text-[#D14A85]" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm group-hover:text-[#D14A85] transition-colors">Book Custom Account</p>
                    <p className="text-[#71717A] text-xs">Request a custom-built artist account</p>
                  </div>
                </button>
                <button onClick={() => startMode("support")} data-testid="support-mode-btn"
                  className="w-full flex items-center gap-3 p-4 rounded-xl bg-[#121214] border border-[#27272A] hover:border-[#3B82F6]/40 transition-all text-left group"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0">
                    <Send className="w-5 h-5 text-[#3B82F6]" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm group-hover:text-[#3B82F6] transition-colors">Contact Support</p>
                    <p className="text-[#71717A] text-xs">Send a message to the team</p>
                  </div>
                </button>
              </div>
            ) : (
              /* Support / Appointment Form */
              <form onSubmit={handleSubmit} className="p-5 space-y-3">
                {mode === "appointment" && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#D14A85]/10 border border-[#D14A85]/20 mb-1">
                    <CalendarCheck className="w-4 h-4 text-[#D14A85] flex-shrink-0" />
                    <p className="text-xs text-[#D14A85]">Custom Account Request</p>
                  </div>
                )}
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name" required
                  className="w-full bg-[#121214] border border-[#27272A] rounded-lg px-4 py-2.5 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50"
                  data-testid="support-name-input"
                />
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Your email" required
                  className="w-full bg-[#121214] border border-[#27272A] rounded-lg px-4 py-2.5 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50"
                  data-testid="support-email-input"
                />
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={mode === "appointment" ? "Tell us about the custom account you'd like..." : "How can we help?"}
                  rows={4} required
                  className="w-full bg-[#121214] border border-[#27272A] rounded-lg px-4 py-2.5 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50 resize-none"
                  data-testid="support-message-input"
                />
                <input ref={attachFileInputRef} type="file" accept="image/*" onChange={handleAttachFile} className="hidden" data-testid="support-attach-input" />
                {attachmentUrl ? (
                  <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-[#121214] border border-[#27272A]" data-testid="support-attachment-preview">
                    <img src={attachmentUrl} alt="attached" className="w-10 h-10 object-contain rounded" />
                    <span className="text-xs text-[#A1A1AA] flex-1 truncate">Screenshot attached</span>
                    <button type="button" onClick={() => setAttachmentUrl(null)} className="text-[#71717A] hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => attachFileInputRef.current?.click()} disabled={uploadingAttachment}
                    className="flex items-center gap-2 text-xs text-[#8A92A0] hover:text-[#EC008C] disabled:opacity-50"
                    data-testid="support-attach-btn"
                  >
                    {uploadingAttachment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                    {uploadingAttachment ? "Uploading…" : "Attach a screenshot (optional)"}
                  </button>
                )}
                <div className="flex gap-2">
                  <button type="button" onClick={() => setMode(null)}
                    className="px-4 py-2.5 rounded-lg text-sm text-[#A1A1AA] hover:text-white bg-[#121214] border border-[#27272A]"
                    data-testid="support-back-btn"
                  >
                    Back
                  </button>
                  <button type="submit" disabled={sending}
                    className="flex-1 bg-[#EC008C] text-[#000000] font-semibold rounded-lg py-2.5 text-sm hover:bg-[#FF3399] disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="support-submit-btn"
                  >
                    {sending ? "Sending..." : <><Send className="w-4 h-4" /> {mode === "appointment" ? "Request Appointment" : "Send Message"}</>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChat;
